import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { TriangleAlert } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { TextLink } from "../components/kit/TextLink";
import { BrandMark } from "../components/brand/BrandMark";

/**
 * ResetPasswordNew — issue #874, slice S3, the forgotten-password
 * NEW-PASSWORD half redrawn to `ForgottenPassword.dc.html`
 * (design-baseline-round-5). Lives behind `newUi("signin")` (`App.js`,
 * reusing S1/S2's flag); `ResetPassword.jsx`, the shipped screen, is
 * untouched and keeps serving `/reset-password` with the flag off.
 *
 * PRESENTATION ONLY. Posts to the SAME endpoint (`POST /auth/reset-
 * password`) with the same payload shape, the same `PASSWORD_MIN_LENGTH`
 * mirror, and the same "sign in with your new password" destination. No
 * behaviour here calls a different endpoint or widens what the server
 * accepts.
 *
 * #775 — THE EXPIRED-LINK STATE, NOW DETECTED ON ARRIVAL, BEFORE ANY
 * TYPING. `ForgottenPassword.dc.html` draws a dedicated "link has run out"
 * state the shipped screen has no equivalent of. Before #775 a dead token
 * failed only on submit into a toast (`formatApiErrorDetail(...) ||
 * "Reset failed"`), so the person only learned the link was dead AFTER
 * typing a password twice. On mount, when a token is present, this screen
 * now calls the READ-ONLY probe `POST /api/auth/reset-password/check`
 * (`api/src/routes/auth.ts`) — it never consumes, rotates, or writes
 * anything, so calling it costs the token nothing. Every dead shade —
 * unknown, expired, used, superseded, disabled — answers the SAME 400
 * body, `RESET_TOKEN_INVALID` (`{ code: "reset_link_invalid", detail:
 * "This reset link is invalid or has expired. Request a new one." }`), by
 * design (#577's own anti-enumeration reasoning: a distinct body per shade
 * would tell a token holder which shade they are holding). This screen
 * matches on the stable `code`, never the `detail` text, so a future
 * wording change cannot silently break the branch (#882/R-775).
 *
 * A link that dies BETWEEN the probe and the submit (the race the probe
 * cannot close) still lands on the drawn state: the submit backstop below
 * matches the SAME `code` on the actual reset call's own failure. See
 * `submit`, below.
 *
 * D48/D57/D55 — no toast on ANY failure now: a mismatch, a too-short
 * password, and a server refusal all render inline, with the D55 marks,
 * replacing the shipped screen's three `toast.error(...)` calls.
 *
 * R-003 — "Update password" is navy, not gold (the artboard spends its one
 * gold on Send reset link, ForgotPasswordNew.jsx's own screen).
 *
 * DESIGNERS' NOTES ARE NOT COPY (S1's B1, checked again for this slice):
 * every `data-dc-note` block is checked against every state below and NONE
 * of them render. Only the SOLID-cornered content nested inside each
 * dashed block is real UI.
 */

/** Mirrors `PASSWORD_MIN_LENGTH` in `api/src/auth/passwordPolicy.ts` — the
 * same client-side mirror the shipped screen keeps. */
const PASSWORD_MIN_LENGTH = 10;

/** #775 — the stable code `RESET_TOKEN_INVALID.code` carries
 * (`api/src/routes/auth.ts`), matched against the server's own response so
 * the drawn "link has run out" state fires ONLY for that one failure
 * shade, never for a mismatch or a too-short password (which never reach
 * the server at all — see `submit`) and never for some OTHER server
 * refusal this constant does not name. */
const RESET_LINK_INVALID_CODE = "reset_link_invalid";

// #1216 — the one "Send me a new one" control, shared by the expired state
// (rp-request-new) and the missing-token state (rp-missing-request-new).
const REQUEST_NEW_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "var(--nw-size-chip)",
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line)",
  color: "var(--nw-navy)",
  fontWeight: 600,
  fontSize: 14,
  borderRadius: "var(--nw-radius-control)",
  cursor: "pointer",
};

/** The one D55-marked inline message box every failure state below
 * renders through — mismatch, too-short, server-refused. */
function InlineError({ testId, children }) {
  return (
    <div
      role="alert"
      data-testid={testId}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "var(--nw-space-inline)",
        padding: "10px 12px",
        background: "var(--nw-line-faint)",
        borderLeft: "3px solid var(--nw-alert)",
        borderRadius: "var(--nw-radius-control)",
      }}
    >
      <TriangleAlert size={14} style={{ color: "var(--nw-alert)", flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
      <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)" }}>{children}</div>
    </div>
  );
}

export default function ResetPasswordNew() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const requestNew = () => nav("/forgot-password");
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // #775 — set when the check probe (on mount) OR the submit backstop
  // gets the server's RESET_TOKEN_INVALID body back; every other failure
  // (client-side mismatch/too-short, or a different server refusal) sets
  // `formError` instead.
  const [expired, setExpired] = useState(false);
  const [formError, setFormError] = useState(null);
  // #775 — true only while the arrival probe is in flight for a PRESENT
  // token; stays false forever once it resolves (or if there is no token
  // to check at all). While true, nothing below renders but the header
  // and the "Step 2 of 2" label — see the JSX's `!checking` guards.
  const [checking, setChecking] = useState(Boolean(token));
  const ranCheck = useRef(false);

  useEffect(() => {
    if (!token || ranCheck.current) return;
    ranCheck.current = true;
    (async () => {
      try {
        // Read-only: never consumes, rotates, or writes anything (see the
        // header comment and the route's own doc comment in auth.ts).
        await api.post("/auth/reset-password/check", { token });
      } catch (err) {
        if (err.response?.data?.code === RESET_LINK_INVALID_CODE) {
          setExpired(true);
        }
        // Any OTHER failure (network, 5xx) — render the form as before;
        // the submit-time backstop below still catches a dead link.
      }
      setChecking(false);
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setExpired(false);
    setFormError(null);
    if (pw !== confirm) {
      setFormError("Passwords don't match");
      return;
    }
    if (pw.length < PASSWORD_MIN_LENGTH) {
      setFormError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters`);
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 1500);
    } catch (err) {
      // #775 — matched on the stable `code`, not the `detail` text (a
      // wording-only edit to either side must not silently break this
      // branch). This is the backstop for a link that dies BETWEEN the
      // arrival probe and this submit — the race the probe cannot close.
      if (err.response?.data?.code === RESET_LINK_INVALID_CODE) {
        setExpired(true);
      } else {
        const detail = formatApiErrorDetail(err.response?.data?.detail);
        setFormError(detail || "Reset failed");
      }
    }
    setBusy(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--nw-page)" }}>
      <span data-testid="reset-password-new-screen" className="sr-only" style={{ color: "var(--nw-ink)" }} aria-hidden="true" />

      <div
        data-testid="reset-password-page"
        // #775 — true only while the arrival probe is in flight, so a
        // screen reader announces this region as busy for exactly the
        // window in which nothing below the label has rendered yet.
        aria-busy={checking ? "true" : undefined}
        className="nw-column"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--nw-space-stack)",
          padding: "var(--nw-space-page)",
          color: "var(--nw-ink)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--nw-ink)" }}>
          <BrandMark size={24} />
          <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 16, color: "var(--nw-navy)" }}>
            Compass
          </span>
        </div>

        {/* "Step 2 of 2 · from the link in the email" is a plain sibling
            of the drawing's own commentary note, not inside it — real UI,
            the section-wide label, same shape as S2's
            SignInLinkCallbackNew.jsx's own "Where the link lands". #775:
            this is the ONE thing that renders while `checking` — the
            probe's own "nothing but the header and this label" contract. */}
        <div className="nw-section" style={{ color: "var(--nw-navy)" }}>
          Step 2 of 2 · from the link in the email
        </div>

        {!checking && !token && (
          <div data-testid="rp-missing-token" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="nw-prose">This link is missing its reset token. Ask for a new one.</div>
            <button type="button" data-testid="rp-missing-request-new" onClick={requestNew} style={REQUEST_NEW_STYLE}>
              Send me a new one
            </button>
          </div>
        )}

        {/* #977 round 2/F1 — a plain flex sibling here added a 14px gap to
            EVERY state (the missing-token and form states grew too), since
            an always-rendered flex child is a flex item whether or not it
            has content. This is a SEPARATE node from the visible
            `rp-expired` block below, using the repo's `sr-only` pattern
            (Tailwind: `position: absolute`, so it is taken out of flow
            entirely and never a flex item, never touching layout or the
            flex gap) rather than `display: contents` (which would strip
            the role/aria-live semantics along with the box). Mounted as
            soon as a token is present — BEFORE the arrival probe settles,
            per the live-region contract (content must already be in the
            DOM when it changes) — and holds the expiry sentence as plain
            text, not the visible block's markup (no duplicate "Send me a
            new one" button announced). */}
        {token && (
          <div role="status" aria-live="polite" data-testid="rp-status" className="sr-only">
            {!checking && expired ? "This link has run out. They last 60 minutes and work once." : ""}
          </div>
        )}

        {!checking && token && expired && (
          <div data-testid="rp-expired" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="nw-prose">This link has run out. They last 60 minutes and work once.</div>
            <button
              type="button"
              data-testid="rp-request-new"
              onClick={requestNew}
              style={REQUEST_NEW_STYLE}
            >
              Send me a new one
            </button>
          </div>
        )}

        {!checking && token && !expired && !done && (
          <>
            <div className="nw-title" style={{ fontSize: 20, letterSpacing: "-0.4px" }}>
              Choose a new password
            </div>

            {/* noValidate — D48/D57 puts EVERY failure inline, driven by
                this file's own JS (mismatch, too-short, server refusal,
                expired). Without it, the browser's own minLength
                constraint on rp-password/rp-confirm intercepts the submit
                event before `submit` ever runs for a too-short value,
                silently leaving whatever error was on screen before
                (caught live, in journey 44, not by source review — a
                native validation bubble instead of the drawn inline
                message). `required`/`minLength` stay on the inputs for
                assistive tech; they simply no longer block submission. */}
            <form onSubmit={submit} noValidate data-testid="reset-password-form" style={{ display: "flex", flexDirection: "column", gap: "var(--nw-space-stack)", color: "var(--nw-ink)" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {/* Review round 3, final narrow round (A3), F9: real
                    <label htmlFor>, same fix as kit/Field.jsx's own
                    (#902/#907). */}
                <label htmlFor="rp-password" className="nw-label" style={{ display: "block" }}>New password</label>
                <input
                  id="rp-password"
                  data-testid="rp-password"
                  type="password"
                  required
                  minLength={PASSWORD_MIN_LENGTH}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="nw-kit-input"
                  style={{
                    width: "100%",
                    height: "var(--nw-size-control)",
                    borderRadius: "var(--nw-radius-control)",
                    border: "1px solid var(--nw-line)",
                    background: "var(--nw-surface)",
                    padding: "0 12px",
                    fontSize: 15,
                    color: "var(--nw-ink)",
                  }}
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label htmlFor="rp-confirm" className="nw-label" style={{ display: "block" }}>Confirm password</label>
                <input
                  id="rp-confirm"
                  data-testid="rp-confirm"
                  type="password"
                  required
                  minLength={PASSWORD_MIN_LENGTH}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="nw-kit-input"
                  style={{
                    width: "100%",
                    height: "var(--nw-size-control)",
                    borderRadius: "var(--nw-radius-control)",
                    border: "1px solid var(--nw-line)",
                    background: "var(--nw-surface)",
                    padding: "0 12px",
                    fontSize: 15,
                    color: "var(--nw-ink)",
                  }}
                />
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "4px 10px",
                    background: "var(--nw-line-faint)",
                    border: "1px solid var(--nw-line)",
                    borderRadius: "var(--nw-radius-control)",
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 800,
                    fontSize: 14,
                    color: "var(--nw-ink)",
                  }}
                >
                  10
                </span>
                {/* #874 S3 review round 1, B2: the drawing's own trailing
                    clause — "Today both checks are toasts raised on submit;
                    the number is the app's own (the API enforces the same
                    one)." — is commentary aimed at whoever reads the
                    drawing (self-describing the mechanism, not naming a
                    UI-visible fact), and after this very build it is also
                    false: D48/D55/D57 replace those toasts with inline
                    messages. It is not wrapped in a data-dc-note, so this
                    reading was not obvious on the first pass — recorded as
                    a departure rather than silently resolved. Only the
                    player-facing sentence renders. */}
                <span className="nw-help">characters at least, and the two must match.</span>
              </div>

              {formError && <InlineError testId="rp-error">{formError}</InlineError>}

              <button
                type="submit"
                data-testid="rp-submit"
                disabled={busy}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: busy ? 10 : 0,
                  minHeight: "var(--nw-size-action)",
                  background: busy ? "var(--nw-surface)" : "var(--nw-navy)",
                  color: busy ? "var(--nw-navy)" : "#FFFFFF",
                  fontWeight: busy ? 600 : 700,
                  fontSize: busy ? 15 : 15,
                  border: busy ? "1px solid var(--nw-navy)" : "none",
                  borderRadius: "var(--nw-radius-control)",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
              >
                {busy && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--nw-navy)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="animate-spin" aria-hidden="true">
                    <path d="M12 3a9 9 0 1 0 9 9" />
                  </svg>
                )}
                {busy ? "Updating…" : "Update password"}
              </button>
            </form>

            {/* #1401 (D363) — the form's labelled exit. "Send me a new one"
                (D353) lives only on the missing-token and expired states;
                this state had no way out but the browser's back button.
                Same kit TextLink as ForgotPasswordNew's fp-back (44px tap
                box, D354); copy is exactly "Back to sign in", no arrow. */}
            <div style={{ display: "flex", justifyContent: "center" }}>
              <TextLink
                as={Link}
                to="/login"
                data-testid="rp-back"
                style={{ fontSize: 13, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "none" }}
              >
                Back to sign in
              </TextLink>
            </div>
          </>
        )}

        {done && (
          <div data-testid="rp-done" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--nw-ok)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              <span className="nw-title" style={{ fontSize: 17 }}>
                Password updated
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
